import React from 'react'
import classnames from 'classnames'
import HeartRating from 'components/heart-rating'
import Link from 'components/link'
import css from './ProductCardV2.styles.scss'
import ProductImage from '../ProductImage/ProductImage'
import PriceTag, { PriceTagProps } from '../PriceTag/PriceTag'
import PromoBadge, { PromoBadgeProps } from '../PromoBadge/PromoBadge'
import {
  ELECTRONIC_GIFT_CARD_MIN,
  ELECTRONIC_GIFT_CARD_MAX,
} from 'shared/constants'

interface ProductCardV2Props {
  brand?: string
  title: string
  imageUrl: string
  path: string
  slugSet?: string
  lazyLoad?: boolean
  isCashFund: boolean
  isCrowdFund: boolean
  isFavor: boolean
  isElectronicGiftCard: boolean
  reviewCount?: number
  rating?: number
  optionsCta?: string
  price?: {
    msrp?: number
    min?: number
    max?: number
    current?: number
  }
  merchandisingMessage?: string
  promotionalBadges?: PromoBadgeProps[]
}

const ProductCardV2: React.FC<ProductCardV2Props> = ({
  brand,
  title,
  imageUrl,
  path,
  slugSet,
  // lazyLoad, // TODO - lazy load images
  isCashFund,
  isCrowdFund,
  isFavor,
  isElectronicGiftCard,
  reviewCount,
  rating,
  optionsCta,
  price,
  merchandisingMessage,
  promotionalBadges,
}) => {
  const priceTagProps = ((): PriceTagProps | null => {
    if (!price) return null

    if (isElectronicGiftCard) {
      return {
        minPrice: ELECTRONIC_GIFT_CARD_MIN,
        maxPrice: ELECTRONIC_GIFT_CARD_MAX,
      }
    }

    return {
      msrp: price?.msrp,
      minPrice: price?.min,
      maxPrice: price?.max,
      currentPrice: price?.current,
    }
  })()

  const supportsRatings = () => {
    return (
      !isCashFund &&
      !isFavor &&
      !isElectronicGiftCard &&
      !isCrowdFund &&
      !!reviewCount
    )
  }

  const hasReviews = () => !!reviewCount

  const overlays = () =>
    promotionalBadges?.map((badge, index) => {
      const position =
        badge.type === 'text' ? ('top-left' as const) : ('top-right' as const)
      return {
        key: `badge-${position}-${index}`,
        position,
        content: <PromoBadge {...badge} />,
      }
    })

  return (
    <div className={classnames(css['ProductCardV2'])}>
      <Link
        to={{
          pathname: path,
          state: { slugSet },
        }}
      >
        <ProductImage
          images={[
            {
              alt: `${brand || ''} ${title}`,
              src: imageUrl,
            },
          ]}
          overlays={overlays()}
        />
        <div className={classnames(css['ProductCardV2__contentWrapper'])}>
          <h3 className="flex col unset-all">
            {brand && (
              <div
                data-testid="brand"
                className={classnames(css['ProductCardV2__brand'])}
              >
                {brand}
              </div>
            )}
            <div
              data-testid="title"
              className={classnames(css['ProductCardV2__title'])}
            >
              {title}
            </div>
          </h3>
          {optionsCta && (
            <div
              data-testid="optionsCta"
              className={classnames(css['ProductCardV2__optionsCta'])}
            >
              {optionsCta}
            </div>
          )}
          {supportsRatings() && hasReviews() && (
            <HeartRating rating={rating || 0} reviewCount={reviewCount || 0} />
          )}
          {priceTagProps && <PriceTag {...priceTagProps} />}
          {merchandisingMessage && (
            <div
              className={classnames(css['ProductCardV2__merchandisingMessage'])}
            >
              {merchandisingMessage}
            </div>
          )}
        </div>
      </Link>
    </div>
  )
}

export default ProductCardV2
